import { useEffect } from "react";
import { Outlet, useOutletContext, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { NoTelegramError, useShopBootstrap } from "../api/bootstrap";
import type { ShopBootstrap } from "../api/bootstrap";
import { BootstrapErrorScreen } from "../components/BootstrapErrorScreen";
import { LoadingScreen } from "../components/LoadingScreen";
import { NoTelegramScreen } from "../components/NoTelegramScreen";
import { OfflineScreen } from "../components/OfflineScreen";
import { ShopUnavailableScreen } from "../components/ShopUnavailableScreen";
import { useTelegramBackButton } from "./useTelegramBackButton";
import { applyTheme } from "../lib/theme";
import { getColorScheme, getThemeParams, onThemeChanged } from "../lib/telegram";

/** useShopContext — данные бутстрапа (магазин, покупатель) в экранах внутри ShopGuard. */
export function useShopContext(): ShopBootstrap {
  return useOutletContext<ShopBootstrap>();
}

/**
 * ShopGuard пускает дальше только после успешной аутентификации по slug из URL.
 * Пока бутстрап не ответил — LoadingScreen; иначе — экран по типу ошибки
 * (docs/07-miniapp-ux.md §2.1); успех — применяет тему магазина и рендерит маршрут.
 */
export function ShopGuard() {
  const { slug } = useParams<{ slug: string }>();
  const bootstrap = useShopBootstrap(slug ?? "");

  // Кнопка «Назад» одна на все экраны витрины — см. useTelegramBackButton.
  useTelegramBackButton();

  const shopTheme = bootstrap.data?.shop.theme;
  useEffect(() => {
    if (!shopTheme) {
      return;
    }
    applyTheme({ themeParams: getThemeParams(), colorScheme: getColorScheme(), shopTheme });
    return onThemeChanged(() => {
      applyTheme({ themeParams: getThemeParams(), colorScheme: getColorScheme(), shopTheme });
    });
  }, [shopTheme]);

  if (bootstrap.isPending) {
    return <LoadingScreen />;
  }

  if (bootstrap.isError) {
    const error = bootstrap.error;
    if (error instanceof NoTelegramError) {
      return <NoTelegramScreen />;
    }
    if (error instanceof ApiError && error.status === 409) {
      return <ShopUnavailableScreen />;
    }
    if (error instanceof ApiError && error.status === 0) {
      return <OfflineScreen onRetry={() => void bootstrap.refetch()} />;
    }
    return <BootstrapErrorScreen onRetry={() => void bootstrap.refetch()} />;
  }

  return <Outlet context={bootstrap.data} />;
}
